import { useOwnership, useStockClassList } from '@commenda/api';
import { useCallback } from 'react';
import { DEFAULT_PERCENT_SCALE } from '../constants';

// Maps the form's per-class % to the holder's share of the corporation's WHOLE
// issued share base — every holder's issued shares across every class, not a
// single class and not the authorized pool. Mirrors the BE
// auto-UBO calc (`ownedShares ÷ total issued shares`). A holder's issued shares
// are reconstructed from their per-class % against that class's authorized count
// (the same conversion the form uses to turn % into a share count).
//
// `stagedAuthorizedShares` supplies the authorized count for a not-yet-created
// class (its `stockClassId` is still null); `excludeStakeholdingId` drops the
// row being edited from the base so its stored shares aren't double-counted.
export function useGetDilutedPercent(
  corporationId: number,
  opts?: {
    stagedAuthorizedShares?: number | null;
    excludeStakeholdingId?: string | null;
  }
) {
  const stagedAuthorizedShares = opts?.stagedAuthorizedShares ?? null;
  const excludeStakeholdingId = opts?.excludeStakeholdingId ?? null;
  const { data: stockClasses } = useStockClassList(corporationId);
  const { data: ownership } = useOwnership(corporationId);

  return useCallback(
    (perClassPercent: number, stockClassId: string | null): number | null => {
      if (stockClasses == null || ownership == null) return null;

      const authorizedFor = (id: string | null): number | null =>
        id != null
          ? (stockClasses.find((sc) => sc.id === id)?.authorizedShares ?? null)
          : null;

      const selectedAuthorized =
        stockClassId != null
          ? authorizedFor(stockClassId)
          : stagedAuthorizedShares;
      if (selectedAuthorized == null || selectedAuthorized <= 0)
        return perClassPercent;
      const holderShares = (perClassPercent / 100) * selectedAuthorized;

      const percentScale = ownership?.percentScale ?? DEFAULT_PERCENT_SCALE;
      const otherIssuedShares = (ownership?.shareholdings ?? [])
        .filter((s) => s.stakeholdingId !== excludeStakeholdingId)
        .reduce((acc, s) => {
          const authorized = authorizedFor(s.stockClass?.id ?? null);
          if (
            authorized == null ||
            authorized <= 0 ||
            s.ownershipPercent == null
          )
            return acc;
          return acc + (s.ownershipPercent / percentScale / 100) * authorized;
        }, 0);

      const totalIssuedShares = otherIssuedShares + holderShares;
      if (totalIssuedShares <= 0) return perClassPercent;
      return (holderShares / totalIssuedShares) * 100;
    },
    [stockClasses, ownership, stagedAuthorizedShares, excludeStakeholdingId]
  );
}
